import React, { useState, useEffect } from 'react';
import { Player } from '../../services/api';

interface UpdatablePlayersListProps {
  players: Player[];
  onOrderChange: (orderedPlayers: Player[]) => void;
  title?: string;
  instructions?: string;
  showChangeIndicator?: boolean;
  disabled?: boolean;
  results?: {
    [playerId: string]: {
      guessed: number;
      actual: number;
      correct: boolean;
    };
  };
}

const UpdatablePlayersList: React.FC<UpdatablePlayersListProps> = ({
  players,
  onOrderChange,
  title = "Order Players",
  instructions = "Drag and drop players to reorder them.",
  showChangeIndicator = true,
  disabled = false,
  results
}) => {
  const [orderedPlayers, setOrderedPlayers] = useState<Player[]>(players);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [hasUserReordered, setHasUserReordered] = useState<boolean>(false);

  // Update internal state when props change - but preserve user reordering
  useEffect(() => {
    // Only reset if user hasn't manually reordered
    if (!hasUserReordered) {
      setOrderedPlayers(players);
    }
  }, [players, hasUserReordered]);

  // Reset the reordering flag when disabled state changes (new phase)
  useEffect(() => {
    if (disabled) {
      setHasUserReordered(false);
    }
  }, [disabled]);

  // Notify parent of changes
  const updatePlayerOrder = (newOrder: Player[]) => {
    setOrderedPlayers(newOrder);
    setHasUserReordered(true);
    onOrderChange(newOrder);
  };

  // Arrow button handlers
  const movePlayer = (fromIndex: number, toIndex: number) => {
    if (disabled || toIndex < 0 || toIndex >= orderedPlayers.length) return;

    const newOrderedPlayers = [...orderedPlayers];
    const [movedPlayer] = newOrderedPlayers.splice(fromIndex, 1);
    newOrderedPlayers.splice(toIndex, 0, movedPlayer);

    updatePlayerOrder(newOrderedPlayers);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    setDragOverIndex(null);
    e.dataTransfer.effectAllowed = 'move';
    // Set a simple identifier instead of HTML content
    e.dataTransfer.setData('text/plain', index.toString());
    
    // Style the drag image
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    if (disabled) return;
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    // Only clear drag over if we're actually leaving the entire item
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newOrderedPlayers = [...orderedPlayers];
    const draggedPlayer = newOrderedPlayers[draggedIndex];
    
    // Remove the dragged player from its current position
    newOrderedPlayers.splice(draggedIndex, 1);
    
    // Insert at the new position
    newOrderedPlayers.splice(dropIndex, 0, draggedPlayer);
    
    updatePlayerOrder(newOrderedPlayers);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Touch handlers for mobile drag and drop
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Handle touch events only on drag handle
  const handleDragHandleTouchStart = (e: React.TouchEvent, index: number) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    
    const touch = e.touches[0];
    
    setTouchStartY(touch.clientY);
    setDraggedIndex(index);
    setDragOverIndex(null);
    setIsDragging(true); // Start drag mode immediately since user touched the drag handle
    
    // Prevent default to stop any scrolling when touching drag handle
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (disabled || !isDragging || draggedIndex === null || touchStartY === null) return;
    
    // We're actively dragging, prevent all defaults and handle dragging
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    
    // Find which element we're over
    const playerItems = document.querySelectorAll('.player-item');
    
    let newDragOverIndex: number | null = null;
    playerItems.forEach((item, idx) => {
      const rect = item.getBoundingClientRect();
      if (touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        newDragOverIndex = idx;
      }
    });
    
    if (newDragOverIndex !== null && newDragOverIndex !== draggedIndex) {
      setDragOverIndex(newDragOverIndex);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (disabled) return;
    
    // Only prevent default if we were actually dragging
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      
      if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
        const newOrderedPlayers = [...orderedPlayers];
        const draggedPlayer = newOrderedPlayers[draggedIndex];
        
        // Remove the dragged player from its current position
        newOrderedPlayers.splice(draggedIndex, 1);
        
        // Insert at the new position
        newOrderedPlayers.splice(dragOverIndex, 0, draggedPlayer);
        
        updatePlayerOrder(newOrderedPlayers);
      }
    }
    
    // Reset all state
    setDraggedIndex(null);
    setDragOverIndex(null);
    setTouchStartY(null);
    setIsDragging(false);
  };



  return (
    <div className="my-5">
      <div 
        className="p-2.5 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50"
        style={{ touchAction: isDragging ? 'none' : 'auto' }}
      >
        {orderedPlayers.map((player, index) => {
          const isBeingDragged = draggedIndex === index;
          const isDragOver = dragOverIndex === index;
          const number = index + 1;
          
          return (
            <div
              key={player.id}
              className={`
                player-item flex items-center p-3 my-1.5 bg-white border border-gray-300 rounded-md transition-all duration-200 select-none min-h-[60px] md:min-h-0 md:p-3
                ${!disabled ? 'hover:bg-gray-50 hover:shadow-md' : 'cursor-default bg-gray-50 border-gray-200'}
                ${isBeingDragged && isDragging ? 'opacity-50 rotate-1 scale-105' : ''}
                ${isDragOver && isDragging ? 'border-blue-500 bg-blue-50 scale-105' : ''}
              `}
              draggable={!disabled}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {!disabled && (
                <div
                  className="px-2 md:px-2 text-gray-400 cursor-grab active:cursor-grabbing hidden md:block"
                  onTouchStart={(e) => handleDragHandleTouchStart(e, index)}
                >
                  <span className="text-base md:text-lg leading-none">⋮⋮</span>
                </div>
              )}
              
              <div className="min-w-[40px] text-center mx-2 md:mx-3">
                <span className="inline-block w-7 h-7 leading-7 bg-blue-500 text-white rounded-full font-bold text-center text-sm">
                  {number}
                </span>
              </div>
              
              <div className="flex-1 flex justify-between items-center">
                <span className="text-base font-medium text-gray-900">{player.name}</span>
                <div className="flex items-center gap-2">
                  {results && results[player.id] && (
                    <span className={`
                      text-sm font-medium px-2 py-1 rounded whitespace-nowrap
                      ${results[player.id].correct
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                      }
                    `}>
                      Actually: <strong>{results[player.id].actual}</strong>
                      {results[player.id].correct ? ' ✅' : ' ❌'}
                    </span>
                  )}

                  {/* Arrow buttons for mobile-friendly reordering */}
                  {!disabled && (
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => movePlayer(index, index - 1)}
                        disabled={index === 0}
                        className={`
                          w-9 h-9 md:w-8 md:h-8 rounded-md flex items-center justify-center text-sm font-bold transition-colors
                          ${index === 0
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700'
                          }
                        `}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => movePlayer(index, index + 1)}
                        disabled={index === orderedPlayers.length - 1}
                        className={`
                          w-9 h-9 md:w-8 md:h-8 rounded-md flex items-center justify-center text-sm font-bold transition-colors
                          ${index === orderedPlayers.length - 1
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700'
                          }
                        `}
                        title="Move down"
                      >
                        ↓
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default UpdatablePlayersList;